import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Pause, Play, Square, Trash2, RefreshCw, CheckCircle2, ArrowLeft, Loader2, Info,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { SandboxBanner, StatusBadge, Modal } from "@/components/outreach/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Stat({ label, value, testid }) {
    return (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
            <p className="text-[10px] uppercase tracking-widest text-text-secondary">{label}</p>
            <p data-testid={testid} className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
        </div>
    );
}

export default function OutreachCampaignDetail() {
    const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [meta, setMeta] = useState(null);
    const [data, setData] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const load = useCallback(async () => {
        try {
            const [d, r, m] = await Promise.all([
                api.outreachCampaign(id),
                api.outreachRecipients(id, { limit: 200 }),
                api.outreachMeta(),
            ]);
            setData(d);
            setRecipients(r.items || []);
            setMeta(m);
        } catch (e) {
            toast.error(e.message || "Load failed");
        } finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Auto-poll every 8s to reflect scheduler events
    useEffect(() => {
        if (!data) return;
        const st = data.campaign.status;
        if (st !== "active" && st !== "paused") return;
        const int = setInterval(load, 8000);
        return () => clearInterval(int);
    }, [data, load]);

    const doAction = async (fn, okMsg) => {
        try {
            await fn(id);
            toast.success(okMsg);
            load();
        } catch (e) {
            toast.error(e.message || "Action failed");
        }
    };

    const markReplied = async (rid) => {
        try {
            await api.outreachRecipientPatch(id, rid, { action: "mark_replied" });
            toast.success("Marked as replied");
            load();
        } catch (e) {
            toast.error(e.message || "Failed");
        }
    };

    const remove = async () => {
        try {
            await api.outreachCampaignDelete(id);
            toast.success("Campaign deleted");
            navigate("/outreach/campaigns");
        } catch (e) {
            toast.error(e.message || "Delete failed");
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main data-testid="campaign-detail-main" className="flex-1 px-4 md:px-6 py-6 max-w-6xl w-full">
                    <SandboxBanner show={meta?.sandbox} />
                    <Link
                        to="/outreach/campaigns"
                        data-testid="campaign-back"
                        className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-main mb-3"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> All campaigns
                    </Link>

                    {loading || !data ? (
                        <div className="p-6 text-sm text-text-secondary">Loading…</div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <StatusBadge status={data.campaign.status} />
                                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">{data.recipients_total} recipients</span>
                                    </div>
                                    <h1 data-testid="campaign-name" className="text-2xl md:text-3xl font-semibold tracking-tight">
                                        {data.campaign.name}
                                    </h1>
                                    <p className="mt-1 text-xs text-text-secondary">
                                        {data.campaign.sender_name} &lt;{data.campaign.sender_email}&gt; · sequence "{data.sequence?.name}"
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <CreditUsageWidget />
                                    {data.campaign.status === "active" && (
                                        <button data-testid="campaign-pause" onClick={() => doAction(api.outreachCampaignPause, "Paused")}
                                            className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs hover:text-text-main"><Pause className="h-3.5 w-3.5" /> Pause</button>
                                    )}
                                    {data.campaign.status === "paused" && (
                                        <button data-testid="campaign-resume" onClick={() => doAction(api.outreachCampaignResume, "Resumed")}
                                            className="inline-flex items-center gap-1 rounded-full gradient-bg-cta px-3 py-1.5 text-xs text-white"><Play className="h-3.5 w-3.5" /> Resume</button>
                                    )}
                                    {(data.campaign.status === "active" || data.campaign.status === "paused") && (
                                        <button data-testid="campaign-stop" onClick={() => doAction(api.outreachCampaignStop, "Stopped")}
                                            className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs hover:text-[var(--color-alert)]"><Square className="h-3.5 w-3.5" /> Stop</button>
                                    )}
                                    <button data-testid="campaign-refresh" onClick={load}
                                        className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs hover:text-text-main"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
                                    <button data-testid="campaign-delete" onClick={() => setConfirmDelete(true)}
                                        className="inline-flex items-center gap-1 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1.5 text-xs hover:bg-[var(--color-alert)]/10"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                                </div>
                            </div>

                            {meta?.sandbox && (
                                <div
                                    data-testid="stats-sandbox-notice"
                                    className="mb-2 inline-flex items-center gap-2 rounded-full border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 px-3 py-1 text-[11px] text-[var(--color-secondary)]"
                                >
                                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-secondary)] animate-pulse-soft" />
                                    Simulated · sandbox stats
                                </div>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 mb-6">
                                <Stat label="Sent" value={data.campaign.stats?.sent || 0} testid="stat-sent" />
                                <Stat label="Delivered" value={data.campaign.stats?.delivered || 0} testid="stat-delivered" />
                                <Stat label="Opened" value={data.campaign.stats?.opened || 0} testid="stat-opened" />
                                <Stat label="Clicked" value={data.campaign.stats?.clicked || 0} testid="stat-clicked" />
                                <Stat label="Replied" value={data.campaign.stats?.replied || 0} testid="stat-replied" />
                                <Stat label="Bounced" value={data.campaign.stats?.bounced || 0} testid="stat-bounced" />
                                <Stat label="Unsub" value={data.campaign.stats?.unsubscribed || 0} testid="stat-unsub" />
                            </div>

                            {/* Timeline */}
                            <div className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                                <p className="text-[10px] uppercase tracking-widest text-text-secondary">Sequence timeline</p>
                                <div className="mt-3 flex flex-wrap gap-4">
                                    {(data.sequence?.steps || []).map((s, i) => (
                                        <div key={i} className="flex items-center gap-2 text-xs">
                                            <span className="h-6 w-6 rounded-full gradient-bg-cta text-white grid place-items-center font-semibold">{s.step_number}</span>
                                            <div>
                                                <p className="font-medium">{s.subject}</p>
                                                <p className="text-text-secondary text-[10px]">
                                                    {i === 0 ? "on launch" : `+${s.delay_days || 0} days`} · {s.send_if_no_reply ? "if no reply" : "always"}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Reply auto-detection notice */}
                            <div className="mb-4 rounded-lg border border-dashed border-[var(--color-border)] bg-transparent px-4 py-2 text-xs text-text-secondary flex items-center gap-2">
                                <Info className="h-3.5 w-3.5" />
                                Reply auto-detection coming in v2. Mark manually below when a reply comes in.
                            </div>

                            {/* Recipients */}
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
                                    <span className="text-sm font-medium">Recipients ({recipients.length})</span>
                                </div>
                                {recipients.length === 0 ? (
                                    <div className="p-8 text-center text-sm text-text-secondary">
                                        No recipients.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="text-[11px] uppercase tracking-widest text-text-secondary/80 border-b border-[var(--color-border)]">
                                                <tr>
                                                    <th className="text-left px-4 py-2 font-normal">Recipient</th>
                                                    <th className="text-left px-4 py-2 font-normal">Company</th>
                                                    <th className="text-center px-4 py-2 font-normal">Step</th>
                                                    <th className="text-left px-4 py-2 font-normal">Status</th>
                                                    <th className="text-right px-4 py-2 font-normal">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recipients.map((r, i) => (
                                                    <motion.tr
                                                        key={r.id}
                                                        initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.2, delay: i * 0.01 }}
                                                        data-testid={`recipient-row-${i}`}
                                                        className="border-b border-[var(--color-border)] last:border-b-0"
                                                    >
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium">{r.first_name} {r.last_name}</p>
                                                            <p className="text-[11px] text-text-secondary">{r.email}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-text-secondary">
                                                            {r.company} <span className="text-[11px]">· {r.job_title}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center tabular-nums text-sm">{r.current_step}</td>
                                                        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                                        <td className="px-4 py-3 text-right">
                                                            {["sent", "opened", "clicked"].includes(r.status) && (
                                                                <button
                                                                    data-testid={`recipient-mark-replied-${i}`}
                                                                    onClick={() => markReplied(r.id)}
                                                                    className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-[11px] text-text-secondary hover:text-[var(--color-success)] hover:border-[var(--color-success)]/40"
                                                                >
                                                                    <CheckCircle2 className="h-3 w-3" />
                                                                    Mark replied
                                                                </button>
                                                            )}
                                                        </td>
                                                    </motion.tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </main>
            </div>

            <Modal
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                title="Delete this campaign?"
                subtitle="Hard delete · GDPR"
                testid="delete-campaign-modal"
            >
                <p className="text-sm text-text-secondary">
                    Removes the campaign and all its recipients, messages, and events.
                    This cannot be undone.
                </p>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setConfirmDelete(false)} className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main">Cancel</button>
                    <button
                        data-testid="delete-campaign-confirm"
                        onClick={remove}
                        className="rounded-full bg-[var(--color-alert)] px-4 py-2 text-sm font-medium text-white hover:opacity-95"
                    >Delete</button>
                </div>
            </Modal>
        </div>
    );
}
